import React, { useState, useCallback, useMemo, useEffect, useRef } from 'react';
import {
  Box,
  Paper,
  Typography,
  Alert,
  CircularProgress,
  Button,
  Fade,
} from '@mui/material';
import { 
  Refresh as RefreshIcon,
  Download as DownloadIcon 
} from '@mui/icons-material';
import {
  UniversalFileViewerProps,
  ViewerError,
  StreamingMetadata,
} from '../../types/viewer.types';
import { useStreamingMetadata } from '../../hooks/useStreamingMetadata';
import ViewerToolbar from './ViewerToolbar';
import PDFViewer from './viewers/PDFViewer';
import ImageViewer from './viewers/ImageViewer';
import MediaPlayer from './viewers/MediaPlayer';
import TextViewer from './viewers/TextViewer';
import OfficeViewer from './viewers/OfficeViewer';
import CodeViewer from './viewers/CodeViewer';
import HTMLViewer from './viewers/HTMLViewer';
import MarkdownViewer from './viewers/MarkdownViewer';
import { userService, organizationService, teamService, subscriptionService, paymentService, invitationService, roleService, contentService, tagService, searchService, authService } from '../../services';

/**
 * Universal File Viewer - Main container component that determines file type 
 * and loads the appropriate specialized viewer
 */
interface FileData {
  url: string;
  name: string;
  type: string;
  mimeType?: string;
  blob?: Blob;
  size?: number;
}

const UniversalFileViewer: React.FC<UniversalFileViewerProps> = ({
  contentId,
  teamId,
  initialMetadata,
  displayName,
  className,
  style,
  height = 600,
  width = '100%',
  showToolbar = true,
  showDownload = true,
  showFullscreen = true,
  onError,
  onLoad,
  onMetadataLoad,
  publicLinkCredentials,
}) => {
  const [zoom, setZoom] = useState<number>(1);
  const [viewerError, setViewerError] = useState<ViewerError | null>(null);
  const [viewerLoading, setViewerLoading] = useState<boolean>(false);
  const [fileData, setFileData] = useState<FileData | null>(null);
  const [nativeRenderMode, setNativeRenderMode] = useState<boolean>(false);
  const [textContent, setTextContent] = useState<string>('');
  const iframeRef = useRef<HTMLIFrameElement>(null);

  // Use the streaming metadata hook
  const { metadata, loading: metadataLoading, error: metadataError, refetch } = useStreamingMetadata(
    contentId,
    teamId,
    initialMetadata
  );

  // Compute the effective display name - combines user's title with file extension
  const effectiveDisplayName = useMemo(() => {
    if (!metadata) return undefined;

    // If displayName is provided (user-set title), combine it with file extension
    if (displayName) {
      // Extract extension from the stored filename
      const storedFileName = metadata.fileName;
      const extensionMatch = storedFileName.match(/\.[^.]+$/);
      const extension = extensionMatch ? extensionMatch[0] : '';

      // Check if displayName already has the extension
      if (extension && !displayName.toLowerCase().endsWith(extension.toLowerCase())) {
        return `${displayName}${extension}`;
      }
      return displayName;
    }

    // Fallback to the stored filename
    return metadata.fileName;
  }, [displayName, metadata]);

  // Check if file is a code file based on extension
  const isCodeFile = useCallback((fileName: string): boolean => {
    const codeExtensions = [
      'js', 'jsx', 'ts', 'tsx', 'py', 'java', 'cpp', 'c', 'h', 'cs', 'go', 'rs',
      'rb', 'php', 'swift', 'kt', 'scala', 'r', 'lua', 'sh', 'bash', 'ps1',
      'dockerfile', 'makefile', 'yml', 'yaml', 'json', 'xml', 'sql', 'proto'
    ];
    const ext = fileName.split('.').pop()?.toLowerCase();
    return codeExtensions.includes(ext || '');
  }, []);

  // Determine if file type supports native rendering
  const supportsNativeRendering = useCallback((mimeType: string, fileName?: string): boolean => {
    // Check if it's a code/html/markdown file - these use specialized viewers
    if (fileName) {
      const ext = fileName.split('.').pop()?.toLowerCase();
      if (ext === 'html' || ext === 'htm' || ext === 'md' || ext === 'markdown') {
        return false; // Use specialized viewers
      }
      if (isCodeFile(fileName)) {
        return false; // Use CodeViewer
      }
      // TIFF files need special decoding - use ImageViewer component
      if (ext === 'tif' || ext === 'tiff') {
        return false; // Use ImageViewer with TIFF decoding
      }
    }

    // Don't use native rendering for HTML or Markdown based on mimeType
    if (mimeType === 'text/html' || mimeType === 'text/markdown' ||
        mimeType === 'application/xhtml+xml' || mimeType === 'text/x-markdown') {
      return false;
    }

    // Don't use native rendering for code-related mimeTypes
    if (mimeType === 'application/javascript' || mimeType === 'application/json' ||
        mimeType === 'application/xml' || mimeType.startsWith('text/x-')) {
      return false;
    }

    // Don't use native rendering for TIFF images - they need special decoding
    if (mimeType === 'image/tiff' || mimeType === 'image/tif') {
      return false; // Use ImageViewer with TIFF decoding
    }

    const nativeFormats = [
      'image/',
      // PDFs are intentionally excluded from native rendering: presigned URLs expire after ~1h,
      // and Chrome's built-in PDF viewer inside an iframe accumulates failed range requests
      // against the expired URL, eventually crashing the IO thread. PDFViewer downloads
      // the full file into a non-expiring blob URL instead.
      // Remove video/ and audio/ from native rendering - they use streaming MediaPlayer
      'text/plain', // Only plain text uses native rendering
    ];

    return nativeFormats.some(format => mimeType.startsWith(format) || mimeType === format);
  }, [isCodeFile]);

  // Get simple file type from MIME type
  const getSimpleFileType = useCallback((mimeType: string, fileName?: string): string => {
    // Check if it's a code file by extension first
    if (fileName && isCodeFile(fileName)) return 'code';
    
    if (mimeType.startsWith('image/')) return 'image';
    if (mimeType === 'application/pdf') return 'pdf';
    if (mimeType.startsWith('video/')) return 'video';
    if (mimeType.startsWith('audio/')) return 'audio';
    
    // Check for HTML and Markdown
    if (mimeType === 'text/html' || fileName?.toLowerCase().endsWith('.html') || fileName?.toLowerCase().endsWith('.htm')) return 'html';
    if (mimeType === 'text/markdown' || fileName?.toLowerCase().endsWith('.md') || fileName?.toLowerCase().endsWith('.markdown')) return 'markdown';
    
    // Check for code file MIME types
    if (mimeType === 'application/javascript' || mimeType === 'application/x-javascript') return 'code';
    if (mimeType === 'application/json') return 'code';
    if (mimeType === 'application/xml' || mimeType === 'text/xml') return 'code';
    if (mimeType.startsWith('text/x-')) return 'code'; // text/x-python, text/x-java, etc.
    
    // General text files
    if (mimeType.startsWith('text/')) return 'text';

    // All Office document types (Word, Excel, PowerPoint) map to 'document'
    if (mimeType.includes('word') || mimeType.includes('document')) return 'document';
    if (mimeType.includes('sheet') || mimeType.includes('excel')) return 'document';
    if (mimeType.includes('presentation') || mimeType.includes('powerpoint')) return 'document';
    if (mimeType.includes('opendocument')) return 'document';

    // For truly unrecognized types, return 'unknown' instead of defaulting to 'document'
    // This prevents trying to convert non-document files
    return 'unknown';
  }, [isCodeFile]);

  // Load file for native rendering
  const loadNativeFile = useCallback(async () => {
    if (!metadata) return;

    try {
      setViewerLoading(true);
      console.log('Loading file for content:', contentId);

      // Check if we have a presigned URL (for public links or direct downloads)
      if (metadata.presignedUrl) {
        // Use the presigned URL directly for native rendering
        const detectedType = getSimpleFileType(metadata.mimeType, metadata.fileName);

        setFileData({
          url: metadata.presignedUrl,
          name: metadata.fileName,
          type: detectedType,
          mimeType: metadata.mimeType,
          size: metadata.fileSize
        });

        setNativeRenderMode(true);
        setViewerLoading(false);

      } else {

        const blob = await contentService.viewContentFile(contentId, teamId);
        const url = URL.createObjectURL(blob);

        const detectedMimeType = blob.type || metadata.mimeType || 'application/octet-stream';
        const detectedType = getSimpleFileType(detectedMimeType, metadata.fileName);

        setFileData({
          url,
          name: metadata.fileName,
          type: detectedType,
          mimeType: detectedMimeType,
          blob,
          size: blob.size
        });

        setNativeRenderMode(true);
        setViewerLoading(false);
      }

    } catch (error) {
      console.debug('File loading failed, falling back to specialized viewers:', error);
      setNativeRenderMode(false);
      setFileData(null);
      setViewerLoading(false);
    }
  }, [contentId, metadata, getSimpleFileType]);

  // Attempt native file loading when metadata becomes available
  // Skip native rendering for public links with streaming strategy (they use specialized viewers)
  useEffect(() => {
    const shouldUseNativeRendering = metadata &&
      supportsNativeRendering(metadata.mimeType, metadata.fileName) &&
      !(metadata.presignedUrl && metadata.downloadStrategy === 'stream');

    if (shouldUseNativeRendering) {
      loadNativeFile();
    }
  }, [metadata, supportsNativeRendering, loadNativeFile]);

  // Cleanup blob URLs
  useEffect(() => {
    return () => {
      if (fileData?.url && fileData.url.startsWith('blob:')) {
        URL.revokeObjectURL(fileData.url);
      }
    };
  }, [fileData?.url]);

  // Load text content when file data changes and it's a text file
  useEffect(() => {
    const loadTextContent = async () => {
      if (fileData && fileData.type === 'text') {
        try {
          if (fileData.blob) {
            // Load from blob
            const text = await fileData.blob.text();
            setTextContent(text);
          } else if (fileData.url) {
            // Load from URL (for presigned URLs)
            const response = await fetch(fileData.url);
            const text = await response.text();
            setTextContent(text);
          }
        } catch (error) {
          console.error('Failed to load text content:', error);
          setTextContent('Error loading text content');
        }
      } else {
        setTextContent('');
      }
    };

    loadTextContent();
  }, [fileData]);

  // Notify parent when metadata is loaded
  React.useEffect(() => {
    if (metadata && onMetadataLoad) {
      onMetadataLoad(metadata);
    }
  }, [metadata, onMetadataLoad]);

  // Combined error handling
  const error = metadataError || viewerError;
  const loading = metadataLoading || viewerLoading;

  // Notify parent of errors
  React.useEffect(() => {
    if (error && onError) {
      onError(error);
    }
  }, [error, onError]);

  // Zoom controls
  const handleZoomIn = useCallback(() => {
    setZoom(prev => Math.min(prev + 0.25, 5));
  }, []);

  const handleZoomOut = useCallback(() => {
    setZoom(prev => Math.max(prev - 0.25, 0.1));
  }, []);

  const handleResetZoom = useCallback(() => {
    setZoom(1);
  }, []);

  const canZoomIn = zoom < 5;
  const canZoomOut = zoom > 0.1;

  // Download handler - explicitly mark as download to track correctly
  const handleDownload = useCallback(() => {
    if (metadata) {
      const url = `/content/${contentId}/download?download=true${teamId ? `&team_id=${teamId}` : ''}`;
      const link = document.createElement('a');
      link.href = url;
      link.download = metadata.fileName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  }, [contentId, metadata]);

  // Fullscreen handler
  const handleFullscreen = useCallback(async () => {
    try {
      const viewerContainer = document.querySelector('[data-viewer-container="true"]') as HTMLElement;
      if (viewerContainer) {
        if (!document.fullscreenElement) {
          await viewerContainer.requestFullscreen();
        } else {
          await document.exitFullscreen();
        }
      }
    } catch (error) {
      console.error('Fullscreen error:', error);
    }
  }, []);

  // Viewer error handler
  const handleViewerError = useCallback((err: ViewerError) => {
    console.error('Viewer error:', err);
    setViewerError(err);
  }, []);

  // Viewer load handler
  const handleViewerLoad = useCallback(() => {
    setViewerError(null);
    setViewerLoading(false);
    if (onLoad) {
      onLoad();
    }
  }, [onLoad]);

  // Native rendering methods from EnhancedFileViewer
  const renderNativeImage = useCallback(() => (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        height: '100%',
        p: 2,
      }}
    >
      <Box
        component="img"
        src={fileData?.url}
        alt={fileData?.name}
        sx={{
          maxWidth: '100%',
          maxHeight: '100%',
          objectFit: 'contain',
          transform: `scale(${zoom})`,
          transformOrigin: 'center center',
          transition: 'transform 0.2s ease-in-out',
        }}
      />
    </Box>
  ), [fileData?.url, fileData?.name, zoom]);

  const renderNativeVideo = useCallback(() => (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        height: '100%',
        p: 2,
      }}
    >
      <Box
        component="video"
        src={fileData?.url}
        controls
        sx={{
          maxWidth: '100%',
          maxHeight: '100%',
          transform: `scale(${zoom})`,
          transformOrigin: 'center center',
          transition: 'transform 0.2s ease-in-out',
        }}
      />
    </Box>
  ), [fileData?.url, zoom]);

  const renderNativeAudio = useCallback(() => (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        height: '100%',
        flexDirection: 'column',
        gap: 3,
      }}
    >
      <Box sx={{ fontSize: '4rem' }}>🎵</Box>
      <Box
        component="audio"
        src={fileData?.url}
        controls
        sx={{ width: '100%', maxWidth: '400px' }}
      />
      <Typography variant="body1" color="text.secondary">
        {fileData?.name}
      </Typography>
    </Box>
  ), [fileData?.url, fileData?.name]);

  const renderNativeText = useCallback(() => (
    <Box
      sx={{
        height: '100%',
        p: 2,
        overflow: 'auto',
        backgroundColor: 'background.default',
      }}
    >
      <Box
        component="pre"
        sx={{
          whiteSpace: 'pre-wrap',
          fontFamily: 'Monaco, Consolas, "Courier New", monospace',
          fontSize: `${0.875 * zoom}rem`,
          lineHeight: 1.5,
          backgroundColor: 'background.paper',
          color: 'text.primary',
          p: 2,
          borderRadius: 1,
          overflow: 'auto',
          border: '1px solid',
          borderColor: 'divider',
        }}
      >
        {textContent || 'Loading text content...'}
      </Box>
    </Box>
  ), [textContent, zoom]);

  const renderNativePdf = useCallback(() => (
    <Box
      sx={{
        height: '100%',
        width: '100%',
        p: 1,
      }}
    >
      <iframe
        ref={iframeRef}
        src={fileData?.url}
        style={{
          width: '100%',
          height: '100%',
          border: 'none',
          borderRadius: '8px',
        }}
        title={`PDF: ${fileData?.name}`}
      />
    </Box>
  ), [fileData?.url, fileData?.name]);

  // Determine which viewer component to render - NATIVE-FIRST APPROACH
  const ViewerComponent = useMemo(() => {
    if (!metadata) {
      return null;
    }

    // NATIVE-FIRST: If we successfully loaded the file natively, use native renderers
    if (nativeRenderMode && fileData) {

      switch (fileData.type) {
        case 'image':
          return renderNativeImage();
        case 'text':
          return renderNativeText();
        // pdf, video, audio, code, html, markdown all use specialized viewers
        default:
          break;
      }
    }

    // FALLBACK: Use specialized viewers for conversion-dependent files

    const baseProps = {
      contentId,
      teamId,
      metadata,
      onError: handleViewerError,
      onLoad: handleViewerLoad,
      onProgress: undefined,
      publicLinkCredentials,
    };

    // First, check if we can determine the correct type from mimeType or fileName
    // This handles cases where the backend might have miscategorized the contentType
    let effectiveContentType = getSimpleFileType(metadata.mimeType, metadata.fileName);

    // Check availableFormats for hints about content type
    if (metadata.availableFormats && metadata.availableFormats.length > 0) {
      if (metadata.availableFormats.includes('text/html') || metadata.availableFormats.includes('html')) {
        if (effectiveContentType === 'text' && metadata.fileName?.toLowerCase().match(/\.(html?|xml|xhtml)$/)) {
          effectiveContentType = 'html';
        }
      }
      if (metadata.availableFormats.includes('text/markdown') || metadata.availableFormats.includes('markdown')) {
        if (effectiveContentType === 'text' && metadata.fileName?.toLowerCase().match(/\.(md|markdown)$/)) {
          effectiveContentType = 'markdown';
        }
      }
    }

    switch (effectiveContentType) {
      case 'pdf':
        // Always use PDFViewer — it downloads to a blob URL that doesn't expire.
        // Never use native iframe directly with a presigned URL (see supportsNativeRendering).
        return <PDFViewer {...baseProps} />;

      case 'document':
        if (metadata.mimeType === 'application/pdf') {
          return <PDFViewer {...baseProps} />;
        }

        // Check if it's an Office document that can be converted
        const officeFormats = [
          // Microsoft Word
          'application/vnd.openxmlformats-officedocument.wordprocessingml.document', // .docx
          'application/msword', // .doc
          // Microsoft Excel
          'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet', // .xlsx
          'application/vnd.ms-excel', // .xls
          // Microsoft PowerPoint
          'application/vnd.openxmlformats-officedocument.presentationml.presentation', // .pptx
          'application/vnd.ms-powerpoint', // .ppt
          // OpenDocument
          'application/vnd.oasis.opendocument.text', // .odt
          'application/vnd.oasis.opendocument.spreadsheet', // .ods
          'application/vnd.oasis.opendocument.presentation', // .odp
          // Rich Text Format
          'application/rtf', // .rtf
          'text/rtf', // .rtf
        ];

        if (officeFormats.includes(metadata.mimeType)) {
          // Office documents can be converted to PDF for viewing
          // Supports both authenticated users and public links
          return <OfficeViewer {...baseProps} />;
        }

        // Unknown document type - return null to show fallback
        return null;

      case 'image':
        // Prefer native image rendering
        return nativeRenderMode && fileData?.type === 'image'
          ? renderNativeImage()
          : (
            <ImageViewer
              {...baseProps}
              zoom={zoom}
              onZoomChange={setZoom}
              allowPan={true}
              allowZoom={true}
              maxZoom={5}
              minZoom={0.1}
            />
          );

      case 'video':
      case 'audio':
        return (
          <MediaPlayer
            {...baseProps}
            metadata={{
              ...metadata,
              contentType: effectiveContentType // Pass the effective content type instead of backend's 'unknown'
            }}
            controls={true}
            autoPlay={false}
            muted={false}
          />
        );

      case 'text':
        // Check if it's actually a code file
        if (metadata.fileName && isCodeFile(metadata.fileName)) {
          return <CodeViewer {...baseProps} />;
        }
        // Prefer native text rendering for plain text
        return nativeRenderMode && fileData?.type === 'text'
          ? renderNativeText()
          : (
            <TextViewer
              {...baseProps}
              highlightSyntax={false}
              showLineNumbers={false}
              wordWrap={true}
              readOnly={true}
            />
          );

      case 'code':
        // Always use CodeViewer for code files
        return <CodeViewer {...baseProps} />;

      case 'html':
        // Scripts and external resources are enabled so HTML files render as intended
        return <HTMLViewer {...baseProps} allowScripts allowExternalResources />;

      case 'markdown':
        // Use MarkdownViewer with proper rendering
        return <MarkdownViewer {...baseProps} />;

      default:
        return null;
    }
  }, [metadata, contentId, teamId, nativeRenderMode, fileData, zoom,
      renderNativeImage, renderNativePdf, renderNativeVideo, renderNativeAudio, renderNativeText,
      handleViewerError, handleViewerLoad, isCodeFile]);

  // Get file type display info
  const getFileTypeDisplay = useCallback((contentType: string, mimeType: string) => {
    const typeMap: Record<string, { icon: string; color: string; label: string }> = {
      document: { icon: '📄', color: '#f44336', label: 'Document' },
      image: { icon: '🖼️', color: '#607d8b', label: 'Image' },
      video: { icon: '🎥', color: '#e91e63', label: 'Video' },
      audio: { icon: '🎵', color: '#9c27b0', label: 'Audio' },
      text: { icon: '📝', color: '#4caf50', label: 'Text' },
      code: { icon: '💻', color: '#2196f3', label: 'Code' },
      html: { icon: '🌐', color: '#ff5722', label: 'HTML' },
      markdown: { icon: '📃', color: '#795548', label: 'Markdown' },
      archive: { icon: '📦', color: '#ff9800', label: 'Archive' },
    };
    return typeMap[contentType] || typeMap.document;
  }, []);

  if (loading) {
    return (
      <Paper
        className={className}
        style={{ ...style, height, width }}
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 2,
        }}
      >
        <CircularProgress size={48} />
        <Typography variant="body2" color="text.secondary">
          Loading file viewer...
        </Typography>
      </Paper>
    );
  }

  if (error) {
    // Enhanced error handling with specific error types
    const getErrorSeverity = (errorCode: string): 'error' | 'warning' | 'info' => {
      switch (errorCode) {
        case 'PERMISSION_DENIED':
        case 'UNAUTHORIZED':
          return 'warning';
        case 'FILE_NOT_FOUND':
        case 'CONTENT_NOT_AVAILABLE':
          return 'info';
        default:
          return 'error';
      }
    };

    const getErrorIcon = (errorCode: string) => {
      switch (errorCode) {
        case 'PERMISSION_DENIED':
        case 'UNAUTHORIZED':
          return '🔒';
        case 'FILE_NOT_FOUND':
        case 'CONTENT_NOT_AVAILABLE':
          return '📄';
        case 'NETWORK_ERROR':
          return '🌐';
        case 'FILE_CORRUPTED':
          return '⚠️';
        default:
          return '❌';
      }
    };

    const getHelpMessage = (errorCode: string): string => {
      switch (errorCode) {
        case 'PERMISSION_DENIED':
          return 'You don\'t have permission to view this file. Contact your administrator if you need access.';
        case 'UNAUTHORIZED':
          return 'Please log in again to access this file.';
        case 'FILE_NOT_FOUND':
          return 'The file may have been moved or deleted.';
        case 'CONTENT_NOT_AVAILABLE':
          return 'The file content is not available for viewing at this time.';
        case 'NETWORK_ERROR':
          return 'Check your internet connection and try again.';
        case 'FILE_CORRUPTED':
          return 'The file appears to be damaged or in an unsupported format.';
        case 'UNSUPPORTED_FORMAT':
          return 'This file type is not supported for viewing. You can download it instead.';
        default:
          return 'An unexpected error occurred while loading the file.';
      }
    };

    const severity = getErrorSeverity(error.code);
    const icon = getErrorIcon(error.code);
    const helpMessage = getHelpMessage(error.code);

    return (
      <Paper
        className={className}
        style={{ ...style, height, width }}
        sx={{ p: 3, display: 'flex', flexDirection: 'column', gap: 3, alignItems: 'center', justifyContent: 'center' }}
      >
        {/* Error Icon */}
        <Box sx={{ fontSize: '4rem' }}>
          {icon}
        </Box>

        {/* Error Alert */}
        <Alert
          severity={severity}
          sx={{ width: '100%', maxWidth: '600px' }}
          action={
            error.retryable ? (
              <Button
                color="inherit"
                size="small"
                onClick={refetch}
                startIcon={<RefreshIcon />}
              >
                Retry
              </Button>
            ) : undefined
          }
        >
          <Typography variant="subtitle1" gutterBottom>
            {error.message}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {helpMessage}
          </Typography>
          {error.details && process.env.NODE_ENV === 'development' && (
            <Typography variant="caption" sx={{ mt: 2, display: 'block', fontFamily: 'monospace' }}>
              Debug Info: {error.code} - {JSON.stringify(error.details, null, 2)}
            </Typography>
          )}
        </Alert>

        {/* Additional Actions */}
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', justifyContent: 'center' }}>
          {/* Download button for when viewing fails */}
          <Button
            variant="outlined"
            startIcon={<DownloadIcon />}
            onClick={handleDownload}
            size="large"
          >
            Download File
          </Button>

          {/* Contact support for permission errors */}
          {(error.code === 'PERMISSION_DENIED' || error.code === 'UNAUTHORIZED') && (
            <Button
              variant="text"
              href="mailto:support@example.com"
              size="large"
            >
              Contact Support
            </Button>
          )}

          {/* Refresh page for network errors */}
          {error.code === 'NETWORK_ERROR' && (
            <Button
              variant="text"
              onClick={() => window.location.reload()}
              size="large"
            >
              Refresh Page
            </Button>
          )}
        </Box>
      </Paper>
    );
  }

  if (!metadata) {
    return (
      <Paper
        className={className}
        style={{ ...style, height, width }}
        sx={{ p: 3, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
      >
        <Typography variant="body2" color="text.secondary">
          No file metadata available
        </Typography>
      </Paper>
    );
  }

  // Calculate effective content type for fallback message (same logic as in ViewerComponent)
  const effectiveContentType = getSimpleFileType(metadata.mimeType, metadata.fileName);
  const typeDisplay = getFileTypeDisplay(effectiveContentType, metadata.mimeType);

  return (
    <Paper
      className={className}
      style={{ ...style, height, width }}
      sx={{
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
      }}
    >
      {showToolbar && (
        <ViewerToolbar
          fileName={metadata.fileName}
          displayName={effectiveDisplayName}
          fileSize={metadata.fileSize}
          contentType={effectiveContentType}
          zoom={zoom}
          canZoomIn={canZoomIn}
          canZoomOut={canZoomOut}
          onZoomIn={handleZoomIn}
          onZoomOut={handleZoomOut}
          onResetZoom={handleResetZoom}
          onDownload={handleDownload}
          onFullscreen={handleFullscreen}
          showDownload={showDownload}
          showFullscreen={showFullscreen}
        />
      )}

      <Box
        data-viewer-container="true"
        sx={{
          flex: 1,
          overflow: 'hidden',
          backgroundColor: '#fafafa',
          position: 'relative',
        }}
      >
        {ViewerComponent ? (
          <>
            <Fade in={true} timeout={300}>
              <Box sx={{ height: '100%', width: '100%' }}>
                {ViewerComponent}
              </Box>
            </Fade>
          </>
        ) : (
          // Fallback/placeholder view
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              height: '100%',
              gap: 3,
              p: 3,
            }}
          >
            <Box sx={{ fontSize: '4rem', color: typeDisplay.color }}>
              {typeDisplay.icon}
            </Box>
            <Typography variant="h6" color="text.secondary">
              {typeDisplay.label}: {metadata.fileName}
            </Typography>
            <Typography variant="body2" color="text.disabled" sx={{ textAlign: 'center', maxWidth: 500 }}>
              {nativeRenderMode
                ? `Unable to display this ${effectiveContentType} file natively`
                : metadata.canConvert
                  ? `This ${effectiveContentType} file will be automatically converted for viewing`
                  : `Preview not available for this file type. You can download it to view with the appropriate application.`}
            </Typography>
            <Button
              variant="contained"
              size="large"
              startIcon={<DownloadIcon />}
              onClick={handleDownload}
              sx={{ mt: 2 }}
            >
              Download File
            </Button>
          </Box>
        )}
      </Box>
    </Paper>
  );
};

export default UniversalFileViewer;